// /vendor/history — completed/closed work, both sources.
//
// Two streams unioned via vendor_jobs:
//   * Assigned: assignment_status='accepted' AND job_status in
//     ('completed','closed','cancelled') — same predicate V2a used.
//   * Self-entered: source='self_entered' AND job_status in
//     ('complete','cancelled') — the 3-value enum's terminal states.
//
// Note the enum mismatch (assigned uses 'completed'/'closed'; self-entered
// uses 'complete') — both render as "Complete" in the UI; the filter
// covers both spellings.

import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, MapPin, CalendarClock, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { formatRelativeDate } from '@/lib/utils'

interface VendorJobRow {
  assignment_id: string
  assignment_status: string | null
  budget_offered: number | string | null
  responded_at: string | null
  field_job_id: string | null
  title: string | null
  job_status: string | null
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_city: string | null
  address_state: string | null
  work_order_number: string | null
  source: 'assigned' | 'self_entered'
}

const ASSIGNED_TERMINAL = new Set(['completed', 'closed', 'cancelled'])
const SELF_TERMINAL     = new Set(['complete', 'cancelled'])

function formatBudget(b: number | string | null): string | null {
  if (b == null) return null
  const n = typeof b === 'string' ? parseFloat(b) : b
  if (!Number.isFinite(n)) return null
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) }
  catch { return `$${n.toFixed(2)}` }
}

export default function VendorHistory() {
  const { data: rows = [], isLoading } = useQuery<VendorJobRow[]>({
    queryKey: ['vendor-history'],
    refetchInterval: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_jobs')
        .select('assignment_id, assignment_status, budget_offered, responded_at, field_job_id, title, job_status, scheduled_date, scheduled_start, scheduled_end, address_line1, address_city, address_state, work_order_number, source')
        .order('responded_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as VendorJobRow[]
    },
  })

  const completed = (rows ?? []).filter(r => {
    if (r.source === 'self_entered') {
      return r.job_status != null && SELF_TERMINAL.has(r.job_status)
    }
    return r.assignment_status === 'accepted' && r.job_status != null && ASSIGNED_TERMINAL.has(r.job_status)
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ height: 70, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }} />
        ))}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>History</h1>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ink-tertiary)' }}>Completed and closed work.</p>
      </header>

      {completed.length === 0 ? (
        <div style={{ padding: 18, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, color: 'var(--ink-secondary)', fontSize: 13 }}>
          Nothing here yet. As jobs wrap up, they'll show up here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {completed.map(r => {
            const isSelf = r.source === 'self_entered'
            const budget = formatBudget(r.budget_offered)
            const addr = !isSelf
              ? ([r.address_line1, r.address_city, r.address_state].filter(Boolean).join(', ') || null)
              : null
            const sched = r.scheduled_date ? formatRelativeDate(r.scheduled_date) : null
            return (
              <Link key={r.assignment_id} to={`/vendor/jobs/${r.assignment_id}`}
                style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6, textDecoration: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 style={{ width: 14, height: 14, color: 'var(--positive)' }} />
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>{r.title ?? (isSelf ? 'Private job' : 'Service visit')}</h3>
                  {isSelf
                    ? <span title="Your private job — staff can't see the details"
                        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 999, fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <Lock style={{ width: 10, height: 10 }} /> Private
                      </span>
                    : (budget && <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)' }}>{budget}</span>)
                  }
                </div>
                {sched && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)' }}>
                    <CalendarClock style={{ width: 12, height: 12 }} /> {sched}
                  </span>
                )}
                {addr && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-tertiary)' }}>
                    <MapPin style={{ width: 12, height: 12 }} /> {addr}
                  </span>
                )}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
